import { loadGraphEntitiesIntoView } from '@squareup/dex-tech-store-access-actions';
import { selectAnyGraphInViewError } from '@squareup/dex-tech-store-access-selectors';
import { ServerAppInitResults } from '@squareup/dex-types-shared-app';
import { Flags } from '@squareup/dex-types-shared-developer-api';
import { UserInfo } from '@squareup/dex-types-shared-developer-data-api';
import { isServerContext } from '@squareup/dex-utils-environment';
import {
  initialStateToResponse,
  settledResultToStoreStateNode,
} from '@squareup/dex-utils-error';
import { withSqAvtPagesDataFetchMiddleware } from '@squareup/dex-utils-shared-next-pages-data-fetch-middleware';
import { runAndRecordFrameworkMetrics } from '@squareup/dex-utils-shared-server-framework-metrics';
import { GetServerSidePropsContext, NextPageContext } from 'next';
import { v4 } from 'uuid';

import { configureApp } from './app-config';
import { initializeApp as initializeServerApp } from './server-app-init';
import {
  setupS2sFetch,
  setupServerLogger,
  setupServerMetrics,
} from './server-module-setup';

const explorerGetInitialProps = (ctx: GetServerSidePropsContext) => {
  withSqAvtPagesDataFetchMiddleware(ctx);

  return runAndRecordFrameworkMetrics(
    { group: 'api-explorer', handler: 'endpoint' },
    async () => {
      const requestId = v4();

      if (isServerContext()) {
        await setupServerMetrics();
        await setupS2sFetch();
        await setupServerLogger();
      }

      const {
        error: configError,
        isLoading: configIsLoading,
        store,
      } = configureApp({ requestId });

      // Config error: There is no infra here.
      if (configError || configIsLoading) {
        // TODO: Redirect to standard error page with error meta.
        return { responseCode: 500, returnVal: {} };
      }

      const initResults = [];

      if (isServerContext()) {
        const serverInitProps = {
          headers: ctx.req?.headers,
          cookies: ctx.req?.cookies,
          requestId,
        };
        initResults.push(initializeServerApp(serverInitProps));
      }

      initResults.push(
        store.dispatch(loadGraphEntitiesIntoView(ctx.query, true))
      );

      const [serverResults] = await Promise.allSettled(initResults);

      const serverInitResults: ServerAppInitResults<UserInfo, Flags> = {
        ...((serverResults &&
          settledResultToStoreStateNode(
            serverResults,
            'api explorer page'
          )) || {
          data: {},
        }),
        requestId,
      };

      const response = initialStateToResponse(
        ctx as unknown as NextPageContext,
        store,
        serverInitResults,
        selectAnyGraphInViewError
      );

      return {
        responseCode: ctx?.res?.statusCode || 200,
        returnVal: response,
      };
    }
  );
};

const graphqlExplorerGetInitialProps = (ctx: GetServerSidePropsContext) => {
  withSqAvtPagesDataFetchMiddleware(ctx);

  return runAndRecordFrameworkMetrics(
    { group: 'graphql-explorer', handler: 'endpoint' },
    async () => {
      const requestId = v4();

      if (isServerContext()) {
        await setupServerMetrics();
        await setupS2sFetch();
        await setupServerLogger();
      }

      const {
        error: configError,
        isLoading: configIsLoading,
        store,
      } = configureApp({ requestId });

      // Config error: There is no infra here.
      if (configError || configIsLoading) {
        // TODO: Redirect to standard error page with error meta.
        return { responseCode: 500, returnVal: {} };
      }

      const initResults = [];

      if (isServerContext()) {
        const serverInitProps = {
          headers: ctx.req?.headers,
          cookies: ctx.req?.cookies,
          requestId,
        };
        initResults.push(initializeServerApp(serverInitProps));
      }

      const [serverResults] = await Promise.allSettled(initResults);

      const serverInitResults: ServerAppInitResults<UserInfo, Flags> = {
        ...((serverResults &&
          settledResultToStoreStateNode(
            serverResults,
            'graphql explorer page'
          )) || {
          data: {},
        }),
        requestId,
      };

      const response = initialStateToResponse(
        ctx as unknown as NextPageContext,
        store,
        serverInitResults,
        selectAnyGraphInViewError
      );

      return {
        responseCode: ctx?.res?.statusCode || 200,
        returnVal: response,
      };
    }
  );
};

export { explorerGetInitialProps, graphqlExplorerGetInitialProps };
